import React from "react";
import Card from "components/card";
import {
  TOP_PRODUCTS_SORT_OPTIONS,
  sortTopProducts,
  truncateProductName,
} from "./topProductsMetrics";

function formatInt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL").format(n);
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

export default function TopProductsTable({
  loading,
  items,
  desde,
  hasta,
  sortBy,
  onSortByChange,
}) {
  const sortedItems = React.useMemo(
    () => sortTopProducts(items || [], sortBy).slice(0, 10),
    [items, sortBy],
  );

  return (
    <Card extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10 xl:col-span-2">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Top 10 productos más vendidos
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Ranking por rango actual con SKU, unidades, ventas totales y ticket promedio por producto.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex flex-col gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
            Ordenar por
            <select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-navy-700 dark:border-white/15 dark:bg-navy-800 dark:text-white"
            >
              {TOP_PRODUCTS_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:border-white/15 dark:bg-white/5 dark:text-gray-300">
            {desde} → {hasta}
          </span>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Cargando top productos…</p>
      ) : sortedItems.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No hay detalle suficiente en DTE aceptados del rango para construir el ranking.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-white/10 dark:text-gray-400">
                <th className="pb-2 pr-3 font-medium">#</th>
                <th className="pb-2 pr-3 font-medium">SKU</th>
                <th className="pb-2 pr-3 font-medium">Producto</th>
                <th className="pb-2 pr-3 text-right font-medium">Unidades</th>
                <th className="pb-2 pr-3 text-right font-medium">Ventas totales</th>
                <th className="pb-2 pr-3 text-right font-medium">Ticket promedio</th>
                <th className="pb-2 text-right font-medium">Líneas</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((row, index) => (
                <tr
                  key={`${row.sku || row.name}-${index}`}
                  className="border-b border-gray-100 dark:border-white/5"
                >
                  <td className="py-2.5 pr-3 font-semibold text-gray-500 dark:text-gray-300">
                    {index + 1}
                  </td>
                  <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">
                    {row.sku || "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-navy-700 dark:text-white">
                    <span title={row.name}>{truncateProductName(row.name, 42)}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-navy-700 dark:text-white">
                    {formatInt(row.units)}
                  </td>
                  <td className="py-2.5 pr-3 text-right text-gray-800 dark:text-gray-100">
                    {formatMoney(row.amount)}
                  </td>
                  <td className="py-2.5 pr-3 text-right text-gray-800 dark:text-gray-100">
                    {formatMoney(row.avgTicket)}
                  </td>
                  <td className="py-2.5 text-right text-gray-600 dark:text-gray-300">
                    {formatInt(row.orders)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
